


// client/src/pages/Investors/InvestorData.jsx

import React, { useState, useMemo } from "react";
import axios from "axios";

import { PageHeader } from "@/components/common/PageHeader";
import { SectionCard } from "@/components/common/SectionCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";

import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import { toast } from "sonner";

import {
  SERIES,
  INVESTORS,
  UPLOADS,
  monthLabel,
} from "@/mock/data";

import { investorService } from "@/services";
import { fmtDate } from "@/lib/format";

import {
  UploadCloud,
  FileSpreadsheet,
  Download,
  Plus,
  Search,
  Pencil,
  Users,
  ShieldAlert,
  Paperclip,
  CheckCircle2,
  Loader2,
  Mail,
  AlertCircle,
} from "lucide-react";

import * as XLSX from "xlsx";

const API_URL =
  process.env.REACT_APP_API_URL || "/api";

/*
 * IMPORTANT:
 *
 * Keep these internal values unchanged because the existing
 * investor/mock/backend logic uses:
 *
 * Resident
 * NRI
 * Lower
 *
 * Only the UI labels say:
 *
 * Resident TDS
 * NRI TDS
 * Lower TDS
 */
const TDS_TYPES = [
  {
    value: "Resident",
    label: "Resident TDS",
  },
  {
    value: "NRI",
    label: "NRI TDS",
  },
  {
    value: "Lower",
    label: "Lower TDS",
  },
];

const normalizeHeader = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const getCellValue = (row, aliases = []) => {
  const normalizedRow = {};

  Object.keys(row || {}).forEach((key) => {
    normalizedRow[normalizeHeader(key)] = row[key];
  });

  for (const alias of aliases) {
    const value = normalizedRow[normalizeHeader(alias)];

    if (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
    ) {
      return value;
    }
  }

  return "";
};

const getTdsLabel = (value) => {
  const item = TDS_TYPES.find((type) => type.value === value);
  return item?.label || value;
};

export default function InvestorData() {
  const activeSeries = SERIES.filter(
    (s) => s.status === "Active"
  );

  const [series, setSeries] = useState(
    activeSeries[0]?.id || ""
  );

  const [tdsType, setTdsType] = useState("Resident");
  const [tab, setTab] = useState("upload");
  const [q, setQ] = useState("");
  const [editOpen, setEditOpen] = useState(null);

  /*
   * Existing Records tab behavior remains category-based.
   * This is NOT used to restrict the bulk-upload emails.
   */
  const filtered = useMemo(
    () =>
      INVESTORS.filter(
        (i) =>
          i.series === series &&
          i.tdsType === tdsType &&
          `${i.name} ${i.pan} ${i.folio} ${i.ifsc} ${i.account}`
            .toLowerCase()
            .includes(q.toLowerCase())
      ),
    [series, tdsType, q]
  );

  return (
    <div>
      <PageHeader
        title="Investor Data"
        subtitle="Manage investor holdings by series and TDS classification. Upload or create manually — every correction is logged."
        actions={
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Download template
          </Button>
        }
      />

      {/* =====================================================
          SERIES + TDS CATEGORY
          ===================================================== */}

      <SectionCard
        title="1. Select series & TDS category"
        description="Select the operational TDS category for this activity. Bulk investor upload is processed using the investor data and registered email addresses."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>NCD Series</Label>

            <Select
              value={series}
              onValueChange={setSeries}
            >
              <SelectTrigger data-testid="inv-series">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {activeSeries.map((s) => (
                  <SelectItem
                    key={s.id}
                    value={s.id}
                  >
                    {s.id} · {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>TDS Category</Label>

            <Select
              value={tdsType}
              onValueChange={setTdsType}
            >
              <SelectTrigger data-testid="inv-tds-type">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {TDS_TYPES.map((type) => (
                  <SelectItem
                    key={type.value}
                    value={type.value}
                  >
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-md bg-accent/50 p-3 text-xs text-primary">
          <ShieldAlert className="h-3.5 w-3.5" />

          {tdsType === "Resident" && (
            <span>
              Resident TDS uses the configured standard rate from Settings.
            </span>
          )}

          {tdsType === "NRI" && (
            <span>
              NRI TDS uses the applicable configured NRI rate.
            </span>
          )}

          {tdsType === "Lower" && (
            <span>
              Lower TDS uses investor-specific certificate details.
              Certificate No., Reference No., Valid From, Valid To,
              TDS Rate and Issuing Authority remain available when
              creating or editing an investor.
            </span>
          )}
        </div>
      </SectionCard>

      {/* =====================================================
          TABS
          ===================================================== */}

      <div className="mt-6">
        <Tabs
          value={tab}
          onValueChange={setTab}
        >
          <TabsList>
            <TabsTrigger
              value="upload"
              data-testid="tab-upload"
            >
              Upload
            </TabsTrigger>

            <TabsTrigger
              value="create"
              data-testid="tab-create"
            >
              Create
            </TabsTrigger>

            <TabsTrigger
              value="records"
              data-testid="tab-records"
            >
              Records
            </TabsTrigger>

            <TabsTrigger
              value="history"
              data-testid="tab-history"
            >
              Upload History
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="upload"
            className="mt-6"
          >
            <UploadTab
              series={series}
              tdsType={tdsType}
            />
          </TabsContent>

          <TabsContent
            value="create"
            className="mt-6"
          >
            <CreateTab
              series={series}
              tdsType={tdsType}
              onCreated={() => setTab("records")}
            />
          </TabsContent>

          <TabsContent
            value="records"
            className="mt-6"
          >
            <SectionCard
              title={`${getTdsLabel(tdsType)} · ${series}`}
              description={`${filtered.length} investor record(s)`}
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="relative w-96">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

                  <Input
                    data-testid="inv-search"
                    value={q}
                    onChange={(e) =>
                      setQ(e.target.value)
                    }
                    placeholder="Search name, PAN, folio, IFSC, account…"
                    className="pl-8"
                  />
                </div>
              </div>

              {filtered.length === 0 ? (
                <EmptyState
                  icon={Users}
                  title="No investors match this combination."
                  description="Try a different series, TDS category, or upload/create data."
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">
                          Investor
                        </th>

                        <th className="px-4 py-3">
                          PAN
                        </th>

                        <th className="px-4 py-3">
                          Folio
                        </th>

                        <th className="px-4 py-3">
                          IFSC
                        </th>

                        <th className="px-4 py-3">
                          Bank Account
                        </th>

                        <th className="px-4 py-3">
                          TDS Type
                        </th>

                        <th className="px-4 py-3">
                          Period
                        </th>

                        <th className="px-4 py-3">
                          Severity
                        </th>

                        <th className="px-4 py-3" />
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-border">
                      {filtered.map((i) => (
                        <tr
                          key={i.id}
                          className="row-hover"
                          data-testid={`inv-row-${i.id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium">
                              {i.name}
                            </div>

                            {i.manuallyUpdated && (
                              <span className="mt-1 inline-block rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary">
                                Manually Updated
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 num text-xs">
                            {i.panMasked}
                          </td>

                          <td className="px-4 py-3 num text-xs">
                            {i.folio}
                          </td>

                          <td className="px-4 py-3 num text-xs">
                            {i.ifsc}
                          </td>

                          <td className="px-4 py-3 num text-xs">
                            {i.accountMasked}
                          </td>

                          <td className="px-4 py-3 text-xs">
                            {getTdsLabel(i.tdsType)}
                          </td>

                          <td className="px-4 py-3 text-xs">
                            {monthLabel(i.month)}
                          </td>

                          <td className="px-4 py-3">
                            <StatusBadge>
                              {i.severity}
                            </StatusBadge>
                          </td>

                          <td className="px-4 py-3">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7"
                              data-testid={`edit-${i.id}`}
                              onClick={() =>
                                setEditOpen(i)
                              }
                            >
                              <Pencil className="mr-1.5 h-3.5 w-3.5" />
                              Edit
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          </TabsContent>

          <TabsContent
            value="history"
            className="mt-6"
          >
            <SectionCard
              title="Upload history"
              description="Every upload is a versioned snapshot. Prior versions are preserved and read-only."
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">
                        Version
                      </th>

                      <th className="px-4 py-3">
                        File
                      </th>

                      <th className="px-4 py-3">
                        Series
                      </th>

                      <th className="px-4 py-3">
                        Period
                      </th>

                      <th className="px-4 py-3">
                        TDS
                      </th>

                      <th className="px-4 py-3">
                        Uploaded
                      </th>

                      <th className="px-4 py-3 text-right">
                        Rows
                      </th>

                      <th className="px-4 py-3">
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-border">
                    {UPLOADS.map((u) => (
                      <tr
                        key={u.id}
                        className="row-hover"
                      >
                        <td className="px-4 py-3 num">
                          v{u.version}

                          {u.isLatest && (
                            <span className="ml-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary">
                              latest
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-xs">
                          {u.filename}
                        </td>

                        <td className="px-4 py-3 num text-xs">
                          {u.series}
                        </td>

                        <td className="px-4 py-3 text-xs">
                          {monthLabel(u.month)}
                        </td>

                        <td className="px-4 py-3 text-xs">
                          {getTdsLabel(u.tdsType)}
                        </td>

                        <td className="px-4 py-3 num text-xs text-muted-foreground">
                          {fmtDate(u.uploadedAt)}
                        </td>

                        <td className="px-4 py-3 num text-right">
                          {u.valid}/{u.total}
                        </td>

                        <td className="px-4 py-3">
                          <StatusBadge>
                            {u.status}
                          </StatusBadge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </TabsContent>
        </Tabs>
      </div>

      <EditInvestorDialog
        open={!!editOpen}
        investor={editOpen}
        onClose={() => setEditOpen(null)}
      />
    </div>
  );
}


/* ============================================================
   UPLOAD TAB
   ============================================================ */

function UploadTab({ series, tdsType }) {
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(0);

  const [dataFile, setDataFile] = useState(null);
  const [investors, setInvestors] = useState([]);

  const [processing, setProcessing] = useState(false);
  const [emailResults, setEmailResults] = useState(null);

  const parseExcel = async (file) => {
    const buffer = await file.arrayBuffer();

    const workbook = XLSX.read(buffer, {
      type: "array",
    });

    const sheetName = workbook.SheetNames[0];

    if (!sheetName) {
      throw new Error(
        "No worksheet was found in the uploaded file."
      );
    }

    const sheet = workbook.Sheets[sheetName];

    const rows = XLSX.utils.sheet_to_json(sheet, {
      defval: "",
    });

    if (!rows.length) {
      throw new Error(
        "The uploaded file does not contain investor records."
      );
    }

    const parsed = rows
      .map((row, index) => ({
        rowNumber: index + 2,

        name: String(
          getCellValue(row, [
            "Investor Name",
            "Investor",
            "Name",
          ])
        ).trim(),

        email: String(
          getCellValue(row, [
            "Email",
            "Email Address",
            "Investor Email",
            "Investor Email Address",
          ])
        ).trim(),

        pan: String(
          getCellValue(row, [
            "PAN",
            "PAN Number",
          ])
        ).trim(),

        folio: String(
          getCellValue(row, [
            "Folio",
            "Folio Number",
          ])
        ).trim(),

        units: getCellValue(row, [
          "Units",
          "Unit",
        ]),

        ifsc: String(
          getCellValue(row, [
            "IFSC",
            "IFSC Code",
          ])
        ).trim(),

        account: String(
          getCellValue(row, [
            "Bank Account",
            "Bank Account Number",
            "Account Number",
          ])
        ).trim(),

        category: String(
          getCellValue(row, [
            "Category",
            "Investor Category",
          ])
        ).trim(),

        allotmentDate: getCellValue(row, [
          "Allotment Date",
        ]),
      }))
      .filter(
        (row) =>
          row.name ||
          row.email ||
          row.pan ||
          row.folio
      );

    if (!parsed.length) {
      throw new Error(
        "No investor records could be read from the file."
      );
    }

    setInvestors(parsed);
    setEmailResults(null);
    setProgress(0);
  };

  const handleFile = async (file) => {
    if (!file) return;

    const validExtensions = [
      ".xlsx",
      ".xls",
      ".csv",
    ];

    const name = file.name.toLowerCase();

    if (
      !validExtensions.some((ext) =>
        name.endsWith(ext)
      )
    ) {
      toast.error(
        "Please upload an Excel or CSV investor file."
      );
      return;
    }

    try {
      setDataFile(file);

      await parseExcel(file);

      toast.success(
        "Investor file validated successfully."
      );
    } catch (error) {
      console.error(error);

      setDataFile(null);
      setInvestors([]);

      toast.error(
        error.message ||
          "Unable to read the investor file."
      );
    }
  };

  const sendInvestorRequest = async (investor) => {
    const response = await axios.post(
      `${API_URL}/investor/send-document-request`,
      {
        investorName: investor.name,
        investorEmail: investor.email,
        series,
        folio: investor.folio,

        /*
         * IMPORTANT:
         *
         * This does NOT mean both documents are mandatory.
         *
         * These are the applicable document choices available
         * to the investor on the secure InvestorUpload page.
         */
        requiredDocuments: [
          "Lower TDS Certificate (Section 197)",
          "Nil TDS Certificate (Form 121)",
        ],
      }
    );

    if (!response.data?.success) {
      throw new Error(
        response.data?.message ||
          "Investor email could not be sent."
      );
    }

    return response.data;
  };

  const doUpload = async () => {
    if (!dataFile) {
      toast.error(
        "Please select an investor file first."
      );
      return;
    }

    if (!investors.length) {
      toast.error(
        "No investor records were found."
      );
      return;
    }

    const invalidEmailRows = investors.filter(
      (investor) =>
        !investor.email ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          investor.email
        )
    );

    if (invalidEmailRows.length > 0) {
      toast.error(
        `${invalidEmailRows.length} investor row(s) have an invalid or missing email address.`
      );
      return;
    }

    setProcessing(true);
    setProgress(0);
    setEmailResults(null);

    const results = [];

    /*
     * Send in small batches rather than creating thousands
     * of simultaneous SMTP requests.
     */
    const BATCH_SIZE = 10;

    for (
      let start = 0;
      start < investors.length;
      start += BATCH_SIZE
    ) {
      const batch = investors.slice(
        start,
        start + BATCH_SIZE
      );

      const batchResults = await Promise.all(
        batch.map(async (investor) => {
          try {
            const response =
              await sendInvestorRequest(
                investor
              );

            return {
              ...investor,
              success: true,
              submissionLink:
                response.submissionLink,
              messageId:
                response.messageId,
            };
          } catch (error) {
            console.error(
              `Email failed for row ${investor.rowNumber}:`,
              error
            );

            return {
              ...investor,
              success: false,
              error:
                error.response?.data?.message ||
                error.message ||
                "Email could not be sent.",
            };
          }
        })
      );

      results.push(...batchResults);

      setProgress(
        Math.round(
          (results.length /
            investors.length) *
            100
        )
      );
    }

    const sent = results.filter(
      (item) => item.success
    ).length;

    const failed = results.filter(
      (item) => !item.success
    ).length;

    setEmailResults({
      total: investors.length,
      sent,
      failed,
      results,
    });

    setProcessing(false);
    setProgress(100);

    if (failed === 0) {
      toast.success(
        `Document request emails sent to all ${sent} investors.`
      );
    } else {
      toast.warning(
        `${sent} emails sent successfully. ${failed} email(s) failed.`
      );
    }
  };

  return (
    <SectionCard
      title={`Upload ${getTdsLabel(tdsType)} investor file`}
      description="Upload the investor data file. Every investor with a registered email address will receive the configured document-request email."
    >
      {/* =====================================================
          UPLOAD AREA
          ===================================================== */}

      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);

          const file =
            e.dataTransfer.files?.[0];

          if (file) {
            handleFile(file);
          }
        }}
        className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
          drag
            ? "border-primary bg-accent"
            : "border-border bg-secondary/40"
        }`}
      >
        <div className="grid h-12 w-12 place-items-center rounded-full bg-white shadow-sm ring-1 ring-border">
          <UploadCloud className="h-5 w-5 text-primary" />
        </div>

        <div className="mt-4 text-sm font-medium">
          Drop Excel or CSV file here
        </div>

        <div className="mt-1 text-xs text-muted-foreground">
          {series} · {getTdsLabel(tdsType)}
        </div>

        <label className="mt-3 cursor-pointer">
          <input
            data-testid="upload-file"
            type="file"
            className="hidden"
            accept=".xlsx,.xls,.csv"
            onChange={(e) =>
              handleFile(
                e.target.files?.[0]
              )
            }
          />

          <span className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium hover:bg-secondary">
            Or browse to select file
          </span>
        </label>

        {dataFile && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-800 ring-1 ring-inset ring-emerald-200">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {dataFile.name}
          </div>
        )}
      </div>

      {/* =====================================================
          IMPORTANT — NO LOWER TDS CERTIFICATE HERE
          ===================================================== */}

      {investors.length > 0 && (
        <div className="mt-5 rounded-md border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-2 text-sm text-blue-900">
            <Mail className="mt-0.5 h-4 w-4" />

            <div>
              <div className="font-medium">
                Investor email workflow
              </div>

              <div className="mt-1 text-xs leading-5 text-blue-800">
                All {investors.length} investor records
                will be processed using the email address
                present in the uploaded file.
                The selected TDS category does not require
                the operations user to upload a certificate
                at this stage.
              </div>

              <div className="mt-2 text-xs text-blue-800">
                Investors will receive a secure link where
                they can provide the applicable document.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          PREVIEW
          ===================================================== */}

      {investors.length > 0 && (
        <div className="mt-6">
          <div className="mb-3">
            <div className="text-sm font-medium">
              Excel validated
            </div>

            <div className="text-xs text-muted-foreground">
              {investors.length} investor row(s) detected
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">
                    Row
                  </th>

                  <th className="px-4 py-3">
                    Investor
                  </th>

                  <th className="px-4 py-3">
                    Email
                  </th>

                  <th className="px-4 py-3">
                    PAN
                  </th>

                  <th className="px-4 py-3">
                    Folio
                  </th>

                  <th className="px-4 py-3">
                    Units
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-border">
                {investors
                  .slice(0, 10)
                  .map((investor) => (
                    <tr
                      key={investor.rowNumber}
                    >
                      <td className="px-4 py-3">
                        {investor.rowNumber}
                      </td>

                      <td className="px-4 py-3 font-medium">
                        {investor.name || "-"}
                      </td>

                      <td className="px-4 py-3 text-xs">
                        {investor.email || "-"}
                      </td>

                      <td className="px-4 py-3 text-xs">
                        {investor.pan || "-"}
                      </td>

                      <td className="px-4 py-3 text-xs">
                        {investor.folio || "-"}
                      </td>

                      <td className="px-4 py-3 text-xs">
                        {investor.units || "-"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {investors.length > 10 && (
            <div className="mt-2 text-xs text-muted-foreground">
              Showing first 10 investors. All{" "}
              {investors.length} investors will be
              processed.
            </div>
          )}
        </div>
      )}

      {/* =====================================================
          SEND BUTTON
          ===================================================== */}

      <div className="mt-6 flex justify-end">
        <Button
          data-testid="btn-simulate-upload"
          onClick={doUpload}
          disabled={
            !dataFile ||
            !investors.length ||
            processing
          }
          className="bg-primary hover:bg-primary/90"
        >
          {processing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending Investor Emails...
            </>
          ) : (
            <>
              <Mail className="mr-2 h-4 w-4" />
              Upload & Send Investor Requests
            </>
          )}
        </Button>
      </div>

      {/* =====================================================
          PROGRESS
          ===================================================== */}

      {processing && (
        <div className="mt-4">
          <Progress value={progress} />

          <div className="mt-1.5 text-xs text-muted-foreground">
            Sending investor document requests...{" "}
            {progress}%
          </div>
        </div>
      )}

      {/* =====================================================
          RESULT
          ===================================================== */}

      {emailResults && (
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3">
          <ResBox
            label="Total Investors"
            value={emailResults.total}
          />

          <ResBox
            label="Emails Sent"
            value={emailResults.sent}
            tone="emerald"
          />

          <ResBox
            label="Email Failures"
            value={emailResults.failed}
            tone="rose"
          />
        </div>
      )}

      {/* =====================================================
          FAILED EMAILS
          ===================================================== */}

      {emailResults?.failed > 0 && (
        <div className="mt-5 rounded-md border border-rose-200 bg-rose-50 p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 text-rose-700" />

            <div>
              <div className="font-medium text-rose-900">
                Some investor emails could not be sent
              </div>

              <div className="mt-1 text-xs text-rose-800">
                Review the failed investor rows and
                retry after correcting the email
                information.
              </div>
            </div>
          </div>

          <div className="mt-3 space-y-2">
            {emailResults.results
              .filter((item) => !item.success)
              .map((item) => (
                <div
                  key={item.rowNumber}
                  className="rounded-md border border-rose-200 bg-white p-3"
                >
                  <div className="text-sm font-medium">
                    Row {item.rowNumber} ·{" "}
                    {item.name || "Unknown Investor"}
                  </div>

                  <div className="text-xs text-muted-foreground">
                    {item.email || "No email"}
                  </div>

                  <div className="mt-1 text-xs text-rose-700">
                    {item.error}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </SectionCard>
  );
}


/* ============================================================
   CREATE TAB
   ============================================================ */

function CreateTab({
  series,
  tdsType,
  onCreated,
}) {
  const [form, setForm] = useState({
    series,
    tdsType,
    lowerTds: {},
  });

  const [certFile, setCertFile] =
    useState(null);

  React.useEffect(() => {
    setForm((f) => ({
      ...f,
      series,
      tdsType,
    }));
  }, [series, tdsType]);

  const set = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: value,
    }));

  const setLower = (key, value) =>
    setForm((f) => ({
      ...f,
      lowerTds: {
        ...(f.lowerTds || {}),
        [key]: value,
      },
    }));

  const save = async () => {
    if (
      !form.name ||
      !form.pan ||
      !form.folio ||
      !form.ifsc ||
      !form.account ||
      !form.units
    ) {
      return toast.error(
        "Please fill all mandatory fields."
      );
    }

    if (tdsType === "Lower") {
      const lower = form.lowerTds || {};

      if (
        !lower.certificateNo ||
        !lower.validFrom ||
        !lower.validTo ||
        !lower.issuer
      ) {
        return toast.error(
          "Lower TDS requires certificate details (No., Valid From/To, Issuing Authority)."
        );
      }

      if (
        lower.rate === null ||
        lower.rate === undefined ||
        lower.rate === "" ||
        Number(lower.rate) < 0
      ) {
        return toast.error(
          "Please enter the applicable Lower TDS rate."
        );
      }

      if (!form.acquisitionDate) {
        return toast.error(
          "Please enter the acquisition date."
        );
      }

      if (!certFile) {
        return toast.error(
          "Please attach the Lower TDS certificate file."
        );
      }
    }

    await investorService.createInvestor({
      ...form,
      certificateFile: certFile,
    });

    toast.success(
      "Investor added — visible on the Records tab."
    );

    setForm({
      series,
      tdsType,
      lowerTds: {},
    });

    setCertFile(null);

    onCreated?.();
  };

  return (
    <SectionCard
      title={`Add ${getTdsLabel(tdsType)} investor`}
      description={`Manually create an investor for ${series}. Marked as Manually Updated in audit.`}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Label>
            Investor Name
            <span className="text-rose-600">
              {" "}*
            </span>
          </Label>

          <Input
            data-testid="c-name"
            value={form.name || ""}
            onChange={(e) =>
              set("name", e.target.value)
            }
          />
        </div>

        <div>
          <Label>
            PAN
            <span className="text-rose-600">
              {" "}*
            </span>
          </Label>

          <Input
            data-testid="c-pan"
            className="num"
            value={form.pan || ""}
            onChange={(e) =>
              set(
                "pan",
                e.target.value.toUpperCase()
              )
            }
          />
        </div>

        <div>
          <Label>
            Folio
            <span className="text-rose-600">
              {" "}*
            </span>
          </Label>

          <Input
            data-testid="c-folio"
            className="num"
            value={form.folio || ""}
            onChange={(e) =>
              set("folio", e.target.value)
            }
          />
        </div>

        <div>
          <Label>
            Units
            <span className="text-rose-600">
              {" "}*
            </span>
          </Label>

          <Input
            data-testid="c-units"
            className="num"
            type="number"
            value={form.units || ""}
            onChange={(e) =>
              set(
                "units",
                Number(e.target.value)
              )
            }
          />
        </div>

        <div>
          <Label>
            IFSC
            <span className="text-rose-600">
              {" "}*
            </span>
          </Label>

          <Input
            data-testid="c-ifsc"
            className="num"
            value={form.ifsc || ""}
            onChange={(e) =>
              set(
                "ifsc",
                e.target.value.toUpperCase()
              )
            }
          />
        </div>

        <div>
          <Label>
            Bank Account
            <span className="text-rose-600">
              {" "}*
            </span>
          </Label>

          <Input
            data-testid="c-account"
            className="num"
            value={form.account || ""}
            onChange={(e) =>
              set("account", e.target.value)
            }
          />
        </div>

        {tdsType === "Lower" ? (
          <>
            <div>
              <Label>
                Acquisition Date
                <span className="text-rose-600">
                  {" "}*
                </span>
              </Label>

              <Input
                type="date"
                data-testid="c-acq-date"
                value={
                  form.acquisitionDate || ""
                }
                onChange={(e) =>
                  set(
                    "acquisitionDate",
                    e.target.value
                  )
                }
              />
            </div>

            <div>
              <Label>
                Transfer Date
              </Label>

              <Input
                type="date"
                data-testid="c-transfer-date"
                value={
                  form.transferDate || ""
                }
                onChange={(e) =>
                  set(
                    "transferDate",
                    e.target.value
                  )
                }
              />
            </div>
          </>
        ) : (
          <div>
            <Label>
              Allotment Date
            </Label>

            <Input
              type="date"
              value={form.allotment || ""}
              onChange={(e) =>
                set(
                  "allotment",
                  e.target.value
                )
              }
            />
          </div>
        )}

        <div>
          <Label>
            Category
          </Label>

          <Input
            value={form.category || ""}
            onChange={(e) =>
              set(
                "category",
                e.target.value
              )
            }
            placeholder="Individual / Corporate / Trust / NRI"
          />
        </div>
      </div>

      {/* =====================================================
          LOWER TDS DETAILS — KEEP THIS FUNCTIONAL
          ===================================================== */}

      {tdsType === "Lower" && (
        <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4">
          <div className="mb-3 flex items-start gap-2 text-sm text-amber-900">
            <ShieldAlert className="mt-0.5 h-4 w-4" />

            <div>
              <div className="font-medium">
                Lower TDS Certificate (Section 197)
              </div>

              <div className="text-xs text-amber-800">
                Maintain the certificate reference
                information and applicable TDS rate.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label>
                Certificate No.
                <span className="text-rose-600">
                  {" "}*
                </span>
              </Label>

              <Input
                data-testid="c-cert-no"
                className="num"
                value={
                  form.lowerTds
                    ?.certificateNo || ""
                }
                onChange={(e) =>
                  setLower(
                    "certificateNo",
                    e.target.value
                  )
                }
              />
            </div>

            <div>
              <Label>
                Reference No.
              </Label>

              <Input
                className="num"
                value={
                  form.lowerTds?.refNo || ""
                }
                onChange={(e) =>
                  setLower(
                    "refNo",
                    e.target.value
                  )
                }
              />
            </div>

            <div>
              <Label>
                Valid From
                <span className="text-rose-600">
                  {" "}*
                </span>
              </Label>

              <Input
                type="date"
                data-testid="c-cert-from"
                value={
                  form.lowerTds
                    ?.validFrom || ""
                }
                onChange={(e) =>
                  setLower(
                    "validFrom",
                    e.target.value
                  )
                }
              />
            </div>

            <div>
              <Label>
                Valid To
                <span className="text-rose-600">
                  {" "}*
                </span>
              </Label>

              <Input
                type="date"
                data-testid="c-cert-to"
                value={
                  form.lowerTds?.validTo || ""
                }
                onChange={(e) =>
                  setLower(
                    "validTo",
                    e.target.value
                  )
                }
              />
            </div>

            <div>
              <Label>
                TDS Rate (%)
                <span className="text-rose-600">
                  {" "}*
                </span>
              </Label>

              <Input
                data-testid="c-cert-rate"
                className="num"
                type="number"
                step="0.01"
                min="0"
                value={
                  form.lowerTds?.rate ?? ""
                }
                onChange={(e) =>
                  setLower(
                    "rate",
                    e.target.value
                  )
                }
                placeholder="e.g. 2 / 3 / 4 / 5"
              />
            </div>

            <div>
              <Label>
                Issuing Authority
                <span className="text-rose-600">
                  {" "}*
                </span>
              </Label>

              <Input
                value={
                  form.lowerTds?.issuer || ""
                }
                onChange={(e) =>
                  setLower(
                    "issuer",
                    e.target.value
                  )
                }
                placeholder="e.g. Income Tax Office"
              />
            </div>

            <div className="md:col-span-2">
              <Label>
                Remarks
              </Label>

              <Input
                value={
                  form.lowerTds?.remarks || ""
                }
                onChange={(e) =>
                  setLower(
                    "remarks",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="md:col-span-2">
              <Label>
                Certificate File
                <span className="text-rose-600">
                  {" "}*
                </span>
              </Label>

              <label className="mt-1 inline-flex cursor-pointer items-center gap-2 rounded-md border border-amber-300 bg-white px-3 py-2 text-xs font-medium text-amber-900 hover:bg-amber-100">
                <input
                  data-testid="c-cert-file"
                  type="file"
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) =>
                    setCertFile(
                      e.target.files?.[0]?.name ||
                        null
                    )
                  }
                />

                <Paperclip className="h-3.5 w-3.5" />

                Attach certificate
                (PDF / image)
              </label>

              {certFile && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-800 ring-1 ring-inset ring-emerald-200">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {certFile}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button
          data-testid="btn-create-investor"
          onClick={save}
          className="bg-primary hover:bg-primary/90"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add investor
        </Button>
      </div>
    </SectionCard>
  );
}


/* ============================================================
   EDIT INVESTOR
   ============================================================ */

function EditInvestorDialog({
  open,
  onClose,
  investor,
}) {
  const [form, setForm] = useState({});
  const [certFile, setCertFile] =
    useState(null);

  React.useEffect(() => {
    if (investor) {
      setForm({
        ...investor,
        lowerTds:
          investor.lowerTds || {},
      });

      setCertFile(null);
    }
  }, [investor]);

  if (!investor) return null;

  const set = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: value,
    }));

  const setLower = (key, value) =>
    setForm((f) => ({
      ...f,
      lowerTds: {
        ...(f.lowerTds || {}),
        [key]: value,
      },
    }));

  const save = async () => {
    if (form.tdsType === "Lower") {
      const lower =
        form.lowerTds || {};

      if (
        !lower.certificateNo ||
        !lower.validFrom ||
        !lower.validTo ||
        !lower.issuer
      ) {
        return toast.error(
          "Lower TDS requires certificate details."
        );
      }

      if (
        lower.rate === null ||
        lower.rate === undefined ||
        lower.rate === ""
      ) {
        return toast.error(
          "Please enter the Lower TDS rate."
        );
      }
    }

    await investorService.updateInvestor(
      investor.id,
      {
        ...form,
        certificateFile:
          certFile ||
          investor.lowerTds?.file,
      }
    );

    toast.success(
      `${investor.id} updated. Marked as Manually Updated.`
    );

    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(openValue) =>
        !openValue && onClose()
      }
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Edit investor · {investor.id}
          </DialogTitle>

          <DialogDescription>
            Correct investor information.
            Lower TDS certificate details remain
            available when applicable.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <Label>Investor Name</Label>

            <Input
              data-testid="edit-name"
              value={form.name || ""}
              onChange={(e) =>
                set("name", e.target.value)
              }
            />
          </div>

          <div>
            <Label>PAN</Label>

            <Input
              className="num"
              value={form.pan || ""}
              onChange={(e) =>
                set(
                  "pan",
                  e.target.value.toUpperCase()
                )
              }
            />
          </div>

          <div>
            <Label>Folio</Label>

            <Input
              className="num"
              value={form.folio || ""}
              onChange={(e) =>
                set(
                  "folio",
                  e.target.value
                )
              }
            />
          </div>

          <div>
            <Label>Units</Label>

            <Input
              className="num"
              type="number"
              value={form.units ?? ""}
              onChange={(e) =>
                set(
                  "units",
                  Number(e.target.value)
                )
              }
            />
          </div>

          <div>
            <Label>IFSC</Label>

            <Input
              data-testid="edit-ifsc"
              className="num"
              value={form.ifsc || ""}
              onChange={(e) =>
                set(
                  "ifsc",
                  e.target.value.toUpperCase()
                )
              }
            />
          </div>

          <div>
            <Label>
              Bank Account Number
            </Label>

            <Input
              data-testid="edit-account"
              className="num"
              value={form.account || ""}
              onChange={(e) =>
                set(
                  "account",
                  e.target.value
                )
              }
            />
          </div>

          <div>
            <Label>TDS Type</Label>

            <Select
              value={form.tdsType}
              onValueChange={(value) =>
                set("tdsType", value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {TDS_TYPES.map((type) => (
                  <SelectItem
                    key={type.value}
                    value={type.value}
                  >
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Category</Label>

            <Input
              value={form.category || ""}
              onChange={(e) =>
                set(
                  "category",
                  e.target.value
                )
              }
            />
          </div>

          {/* =================================================
              LOWER TDS EDIT DETAILS
              ================================================= */}

          {form.tdsType === "Lower" && (
            <>
              <div className="md:col-span-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                Lower TDS certificate details are
                maintained against the investor.
              </div>

              <div>
                <Label>
                  Certificate No.
                </Label>

                <Input
                  className="num"
                  value={
                    form.lowerTds
                      ?.certificateNo || ""
                  }
                  onChange={(e) =>
                    setLower(
                      "certificateNo",
                      e.target.value
                    )
                  }
                />
              </div>

              <div>
                <Label>
                  Reference No.
                </Label>

                <Input
                  className="num"
                  value={
                    form.lowerTds?.refNo || ""
                  }
                  onChange={(e) =>
                    setLower(
                      "refNo",
                      e.target.value
                    )
                  }
                />
              </div>

              <div>
                <Label>
                  Valid From
                </Label>

                <Input
                  type="date"
                  value={
                    form.lowerTds
                      ?.validFrom || ""
                  }
                  onChange={(e) =>
                    setLower(
                      "validFrom",
                      e.target.value
                    )
                  }
                />
              </div>

              <div>
                <Label>
                  Valid To
                </Label>

                <Input
                  type="date"
                  value={
                    form.lowerTds?.validTo || ""
                  }
                  onChange={(e) =>
                    setLower(
                      "validTo",
                      e.target.value
                    )
                  }
                />
              </div>

              <div>
                <Label>
                  TDS Rate (%)
                </Label>

                <Input
                  className="num"
                  type="number"
                  step="0.01"
                  min="0"
                  value={
                    form.lowerTds?.rate ?? ""
                  }
                  onChange={(e) =>
                    setLower(
                      "rate",
                      e.target.value
                    )
                  }
                />
              </div>

              <div>
                <Label>
                  Issuing Authority
                </Label>

                <Input
                  value={
                    form.lowerTds?.issuer || ""
                  }
                  onChange={(e) =>
                    setLower(
                      "issuer",
                      e.target.value
                    )
                  }
                />
              </div>

              <div className="md:col-span-2">
                <Label>
                  Certificate File
                </Label>

                <label className="mt-1 inline-flex cursor-pointer items-center gap-2 rounded-md border border-amber-300 bg-white px-3 py-2 text-xs font-medium text-amber-900 hover:bg-amber-100">
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) =>
                      setCertFile(
                        e.target.files?.[0]
                          ?.name || null
                      )
                    }
                  />

                  <Paperclip className="h-3.5 w-3.5" />

                  {certFile
                    ? `Replace (${certFile})`
                    : "Attach / replace certificate"}
                </label>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            data-testid="btn-save-edit"
            onClick={save}
            className="bg-primary hover:bg-primary/90"
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ============================================================
   RESULT BOX
   ============================================================ */

const ResBox = ({
  label,
  value,
  tone,
}) => {
  const cls =
    {
      emerald:
        "border-emerald-200 bg-emerald-50 text-emerald-800",

      rose:
        "border-rose-200 bg-rose-50 text-rose-800",

      amber:
        "border-amber-200 bg-amber-50 text-amber-800",
    }[tone] || "border-border";

  return (
    <div
      className={`rounded-md border p-4 ${cls}`}
    >
      <div className="text-xs">
        {label}
      </div>

      <div className="num mt-1 text-xl font-semibold">
        {value}
      </div>
    </div>
  );
};